import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, CreditCard, AlertCircle, CalendarDays, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { formatDateFromIso } from '@/lib/datetime';

export default function AdminDashboard() {
    const [stats, setStats] = useState({ gyms: 0, owners: 0, plans: 0 });
    const [ownersWithoutPlan, setOwnersWithoutPlan] = useState([]);
    const [recentSubscriptions, setRecentSubscriptions] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [gymsRes, ownersRes, plansRes] = await Promise.all([
                    api.get('/admin/gyms?per_page=1'),
                    api.get('/admin/owners?per_page=1'),
                    api.get('/admin/plans'),
                ]);
                setStats({
                    gyms: gymsRes.data.total || 0,
                    owners: ownersRes.data.total || 0,
                    plans: plansRes.data.data?.length || 0,
                });
                setPlans(plansRes.data.data || []);

                // Fetch owners without plans
                const ownersAll = await api.get('/admin/owners?per_page=100');
                const withoutPlan = (ownersAll.data.data || []).filter(o => !o.subscription?.plan);
                setOwnersWithoutPlan(withoutPlan);

                // Fetch recent subscriptions
                const subs = [];
                (ownersAll.data.data || []).forEach(o => {
                    if (o.subscription) {
                        subs.push({
                            owner: o,
                            subscription: o.subscription,
                        });
                    }
                });
                setRecentSubscriptions(subs.slice(0, 5));
            } catch {
                // silent
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAssignPlan = async (ownerId, planId) => {
        try {
            await api.post(`/admin/owners/${ownerId}/assign-plan`, { plan_id: planId });
            setOwnersWithoutPlan(prev => prev.filter(o => o.id !== ownerId));
            // Refresh subscriptions
            const ownersAll = await api.get('/admin/owners?per_page=100');
            const subs = [];
            (ownersAll.data.data || []).forEach(o => {
                if (o.subscription) {
                    subs.push({ owner: o, subscription: o.subscription });
                }
            });
            setRecentSubscriptions(subs.slice(0, 5));
        } catch {}
    };

    const statCards = [
        { title: 'Gimnasios', value: stats.gyms, icon: Building2 },
        { title: 'Propietarios', value: stats.owners, icon: Users },
        { title: 'Planes', value: stats.plans, icon: CreditCard },
    ];

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold uppercase tracking-wide">Dashboard</h1>
            <div className="grid gap-4 md:grid-cols-3">
                {statCards.map(card => (
                    <Card key={card.title} className="bg-[#0a0a0a] border-hairline-dark">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-white/60">{card.title}</CardTitle>
                            <card.icon className="h-5 w-5 text-white" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold">
                                {loading ? '...' : card.value}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Owners without plan */}
            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader className="flex flex-row items-center gap-3 pb-2">
                    <AlertCircle className="h-5 w-5 text-white" />
                    <CardTitle className="text-lg">Propietarios sin plan</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-4">Cargando...</p>
                    ) : ownersWithoutPlan.length === 0 ? (
                        <div className="flex items-center gap-2 text-sm text-white/60 py-4">
                            <CheckCircle2 className="h-4 w-4 text-white" />
                            Todos los propietarios tienen un plan asignado.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {ownersWithoutPlan.map(owner => (
                                <div key={owner.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border border-hairline-dark hover:bg-white/5">
                                    <div>
                                        <p className="font-medium">{owner.name}</p>
                                        <p className="text-sm text-white/60">{owner.email}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <select
                                            className="h-9 rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 py-1 text-sm text-white"
                                            defaultValue=""
                                            onChange={(e) => {
                                                const planId = e.target.value;
                                                if (planId) handleAssignPlan(owner.id, planId);
                                            }}
                                        >
                                            <option value="" className="bg-[#0a0a0a] text-white">Seleccionar plan...</option>
                                            {plans.map(p => (
                                                <option key={p.id} value={p.id} className="bg-[#0a0a0a] text-white">{p.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Recent subscriptions */}
            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader className="flex flex-row items-center gap-3 pb-2">
                    <CalendarDays className="h-5 w-5 text-white" />
                    <CardTitle className="text-lg">Suscripciones recientes</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-4">Cargando...</p>
                    ) : recentSubscriptions.length === 0 ? (
                        <p className="text-sm text-white/60 py-4">No hay suscripciones activas.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[500px]">
                                <thead>
                                    <tr className="border-b border-hairline-dark">
                                        <th className="text-left py-2 px-2 font-medium">Propietario</th>
                                        <th className="text-left py-2 px-2 font-medium">Plan</th>
                                        <th className="text-left py-2 px-2 font-medium">Estado</th>
                                        <th className="text-left py-2 px-2 font-medium">Vencimiento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentSubscriptions.map(({ owner, subscription }) => (
                                        <tr key={subscription.id} className="border-b border-hairline-dark last:border-0 hover:bg-white/5">
                                            <td className="py-2 px-2 font-medium">{owner.name}</td>
                                            <td className="py-2 px-2">{subscription.plan?.name || '-'}</td>
                                            <td className="py-2 px-2">
                                                <Badge variant={subscription.status === 'active' ? 'success' : 'secondary'}>
                                                    {subscription.status}
                                                </Badge>
                                            </td>
                                            <td className="py-2 px-2">
                                                {subscription.is_lifetime
                                                    ? <span className="text-white font-medium">∞</span>
                                                    : subscription.ends_at
                                                        ? formatDateFromIso(subscription.ends_at)
                                                        : '-'
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
